/**
 * Job-detail nudge for synonym suggestions (features/synonym_suggestions.md).
 *
 * "N synonym suggestions for this job" — dismissible; Review expands the
 * Add/Decline list inline. Add re-reads the resume and PUTs it back with ONLY
 * the synonym sets changed (same pattern as saving implied facts), so the
 * normal resume save runs and tags refresh as usual. Nothing already generated
 * is rewritten — the new set applies on the next generation.
 */
import { useMemo, useState } from 'react';
import { Lightbulb, X, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../services/api';
import type { SynonymSet, SynonymSuggestion } from '../types';
import { SynonymSuggestionList } from './SynonymSuggestionList';
import { applySuggestion, declineSuggestion, filterSuggestions } from '../utils/synonymSuggestions';

interface JobSynonymSuggestionsProps {
  jobid: string;
  resumeid?: string;
  suggestions?: SynonymSuggestion[];
}

function dismissKey(jobid: string) {
  return `ss_synsug_dismissed_${jobid}`;
}

export function JobSynonymSuggestions({ jobid, resumeid, suggestions = [] }: JobSynonymSuggestionsProps) {
  const signature = suggestions.map(s => s.pair_key).sort().join('|');
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(dismissKey(jobid)) === signature; } catch { return false; }
  });
  const [open, setOpen] = useState(false);
  // Sets as of our last Add (unknown until then — the server already filtered).
  const [sets, setSets] = useState<SynonymSet[] | undefined>(undefined);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  const visible = useMemo(
    () => filterSuggestions(suggestions, sets, hidden),
    [suggestions, sets, hidden],
  );

  if (!resumeid || dismissed || (visible.length === 0 && !added && !error)) return null;

  function dismiss() {
    setDismissed(true);
    try { localStorage.setItem(dismissKey(jobid), signature); } catch { /* per-viewer convenience only */ }
  }

  async function handleAdd(s: SynonymSuggestion) {
    if (!resumeid) return;
    setBusyKey(s.pair_key);
    setError(null);
    try {
      const current = await api.getResume(resumeid);
      const next = applySuggestion(current.resumejson.synonyms, s);
      await api.updateResume(resumeid, { ...current.resumejson, synonyms: next });
      setSets(next);
      setAdded(true);
    } catch (err) {
      console.error('Failed to add synonym suggestion:', err);
      setError('Could not add that synonym — please try again.');
    } finally {
      setBusyKey(null);
    }
  }

  async function handleDecline(s: SynonymSuggestion) {
    setBusyKey(s.pair_key);
    setError(null);
    const res = await declineSuggestion(
      s,
      { decline: terms => api.declineSynonymSuggestion(terms) },
      key => setHidden(prev => new Set(prev).add(key)),
      key => setHidden(prev => { const n = new Set(prev); n.delete(key); return n; }),
    );
    setBusyKey(null);
    if (!res.ok) {
      console.error('Failed to decline synonym suggestion:', res.error);
      setError('Could not decline that suggestion — please try again.');
    }
  }

  const n = visible.length;
  return (
    <div className="px-4 py-2 border-b bg-purple-50" data-testid="job-synonym-suggestions">
      <div className="flex items-center gap-2">
        <Lightbulb className="w-4 h-4 text-purple-500 shrink-0" aria-hidden="true" />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          data-testid="job-synonym-suggestions-toggle"
          className="flex items-center gap-1 text-sm text-purple-800 hover:text-purple-900 font-medium"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {n > 0
            ? `${n} synonym suggestion${n === 1 ? '' : 's'} for this job`
            : 'Synonym suggestions reviewed'}
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={dismiss}
          className="p-0.5 text-purple-400 hover:text-purple-700 rounded"
          aria-label="Dismiss synonym suggestions"
          title="Hide this reminder (the suggestions stay in Edit Resume → Define Synonyms)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          {n > 0 && (
            <>
              <p className="text-xs text-gray-600">
                Your resume uses a different word than this job for what looks like the same thing.
                Add a pair only if the words are truly interchangeable for you — it becomes a synonym set on your resume.
              </p>
              <SynonymSuggestionList
                suggestions={visible}
                sets={sets}
                onAdd={handleAdd}
                onDecline={handleDecline}
                busyKey={busyKey}
              />
            </>
          )}
          {added && (
            <p className="text-xs text-green-700" data-testid="job-synonym-suggestion-added">
              Saved to your resume&apos;s synonyms. It applies the next time you generate — resumes already generated are not changed.
            </p>
          )}
          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
